import { findItemViewModel, isComp } from 'components/Sell/utils/common'
import { isOpenItemId } from 'constant/magicNumber'
import Decimal from 'decimal.js'
import {
    DiscountContract,
    DiscountLevel,
    DiscountType,
    ModifierOptionType,
    OrderBaseItemContract,
    OrderDiscountContract,
    OrderDiscountModel,
    OrderDiscountType,
    OrderLineComboVariantOptionSlimModel,
    OrderLineContract,
    OrderLineDiscountSlimModel,
    OrderLineItemContract,
    OrderLineModifierProcessContract,
    OrderLineModifierSlimModel,
    OrderLineLookupContract,
    OrderLineStatus,
    OrderPaymentStatus,
    PaymentContract,
    PaymentStatus,
    VoidCompReasonModel,
    OrderLookupContract,
    OrderSaleDetail,
} from 'models'
import { ComboItemViewmodel, ItemViewModel } from 'store/models/sellModels'
import { PendingTransactionEntity } from 'utilities/offlineDb/models'
import { getItemPrice, getModifierOptionPrice } from 'utilities/price/getPrice'
import { v4 } from 'uuid'
import { calculateDiscountAmount } from 'views/Sale/utils/orderCalculation/discountCalculation'

import { ISaleDependencies } from 'views/Sale/contexts/SaleDependenciesContext'
interface OfflineOrderContract
    extends Omit<OrderLookupContract, 'balanceTotalDue'> {
    balanceTotalDue?: number
    sale?: OrderSaleDetail
}
/**
 * Convert `PendingRecordEntity` to OrderLookupContract using SellState to resolve extra fields e.g., Item name, modifier option name
 * @param dependencies `SellState` used as reference. Normally, you can use SellState from Redux store.
 * @param pendingRecord
 * @returns OrderSlim
 */
export const convertPendingRecordToSlimModel = (
    dependencies: ISaleDependencies,
    pendingRecord: PendingTransactionEntity,
): OfflineOrderContract => {
    const order = pendingRecord.order
    const serviceChargeAmount = new Decimal(order.subtotal!)
        .mul((order.serviceChargeRate ?? 0) / 100)
        .toNumber()

    const orderSlim: OfflineOrderContract = {
        orderLines: order.orderLines.map((o) =>
            convertOfflineOrderLineToSlimModel(dependencies, o),
        ),
        // order discount must be resolved after resolved line discounts
        orderDiscounts: [],
        customer: {
            givenName: order.customerName,
        },
        voidUserName: '#voidUserName',
        userName: '(local device)',
        orderID: pendingRecord.orderID,
        orderNo: '[to be generated]',
        orderDateTime: order.orderDateTime,
        customerID: order.customerID,
        orderFulfillmentTypeID: order.orderFulfillmentTypeID,
        subtotal: order.subtotal,
        discountAmount:
            order.subtotal! +
            order.totalTaxAmount! +
            serviceChargeAmount -
            order.totalDueAmount!,
        totalTaxAmount: order.totalTaxAmount,
        totalDueAmount: order.totalDueAmount,
        note: order.note,
        status: pendingRecord.orderStatus,
        orderPaymentStatus: pendingRecord.orderPaymentStatus,
        serviceChargeAmount,
        serviceChargeRate: order.serviceChargeRate ?? 0,

        // specify more fields here if needs
    }

    // resolve order-level discount
    const sumLineDiscounts = orderSlim.orderLines!.reduce(
        (acc, line) => acc + line.orderLineDiscountAmount!,
        0,
    )
    const orderSubtotalAfterItemDiscount =
        orderSlim.subtotal! - sumLineDiscounts
    orderSlim.orderDiscounts = resolveDiscounts(
        dependencies,
        false,
        orderSubtotalAfterItemDiscount,
        1,
        order.discounts ?? [],
    ).map(toDiscountContract)

    const orderSubtotalAfterNormalDiscount =
        orderSubtotalAfterItemDiscount -
        orderSlim.orderDiscounts.reduce((acc, d) => acc + d.discountAmount!, 0)

    // resolve order comp discount
    if (order.voidComp) {
        if (isComp(order.voidComp)) {
            orderSlim.isComp = true
            orderSlim.voidCompNotes = order.voidComp.note
            orderSlim.voidCompReason = order.voidComp.name
            orderSlim.voidCompReasonID = order.voidComp.voidCompReasonID

            const compDiscount = resolveCompDiscount(
                orderSubtotalAfterNormalDiscount,
                1,
                order.voidComp,
            )
            orderSlim.orderDiscounts.push(toDiscountContract(compDiscount))
        }
    }

    // resolve `sales.payments` as it needed to resume uncompleted partial payment
    // generate virtual payment records as if it processed/saved in server side
    if (
        pendingRecord.payments &&
        pendingRecord.payments.length > 0 &&
        pendingRecord.order.orderPaymentStatus !== OrderPaymentStatus.Paid
    ) {
        const virtualPaymentRecords: PaymentContract[] = []
        pendingRecord.payments.forEach((p) => {
            // Receive record
            const receivePaymentRecordID = v4()
            virtualPaymentRecords.push({
                paymentID: receivePaymentRecordID,
                paymentAccountID: p.payments![0].paymentAccountID,
                paymentTypeID: p.payments![0].paymentTypeID,
                status: PaymentStatus.Received,
                amount: p.payments![0].tenderedAmount,
            })

            // Return record
            if (p.payments![0].changeAmount! > 0) {
                virtualPaymentRecords.push({
                    paymentID: v4(),
                    referencePaymentID: receivePaymentRecordID,
                    paymentAccountID: p.payments![0].paymentAccountID,
                    paymentTypeID: p.payments![0].paymentTypeID,
                    status: PaymentStatus.Returned,
                    amount: p.payments![0].changeAmount,
                })
            }
        })

        orderSlim.sale = {
            balanceDueAmount: order.totalDueAmount,
            payments: virtualPaymentRecords,
        }
    }

    return orderSlim
}

// #region Private functions
const resolveBaseItem = (baseItem: ItemViewModel): OrderBaseItemContract => {
    return {
        itemID: baseItem.itemId,
        name: baseItem.name,
        displayName: baseItem.displayName,
        itemVariantGroups: baseItem.variantGroups.map((v) => ({
            variantGroup: v,
        })),
    }
}

const resolveLineItem = (
    refSellState: ISaleDependencies,
    itemID: string,
): OrderLineItemContract => {
    const findResult = findItemViewModel(refSellState.itemsMap, itemID)
    if (!findResult) return { name: '<unknown item>' }

    const { parentItem, searchItem } = findResult

    return {
        name: searchItem.name,
        displayName: searchItem.displayName,
        baseItem: parentItem ? resolveBaseItem(parentItem) : undefined,
        combinationIndices: searchItem.combinationIds?.join('*') ?? '',
    }
}

const resolveComboVariantOptions = (
    comboLine: OrderLineContract,
    refComboItemViewModel: ComboItemViewmodel,
): OrderLineComboVariantOptionSlimModel[] => {
    if (!comboLine.orderLineVariantOptionIds) return []

    const allVariantOptionApplyToThisComboChoiceItem = refComboItemViewModel.variantGroups.flatMap(
        (vg) => vg.variantOptions,
    )

    return comboLine.orderLineVariantOptionIds.map<
        OrderLineComboVariantOptionSlimModel
    >((variantOptionId) => {
        const matchedVariantOption = allVariantOptionApplyToThisComboChoiceItem.find(
            (v) => v?.variantOptionID === variantOptionId,
        )
        if (!matchedVariantOption)
            return {
                priceAdd: 0,
                totalAddPrice: 0,
                variantOption: {
                    name: '#unknown var: ' + variantOptionId,
                    sortNumber: 1,
                },
            }

        return {
            priceAdd: matchedVariantOption.addPrice,
            totalPriceAdd: matchedVariantOption.addPrice,
            variantOption: {
                name: matchedVariantOption.name,
                variantOptionID: variantOptionId,
                sortNumber: matchedVariantOption.sortNumber,
            },
        }
    })
}

/**
 * Convert `lineModifier` to `OrderLineModifierSlimModel` model. Mainly used by `ModifyDisplay` component .
 * @param refSellState
 * @param lineMenuID
 * @param lineModifier
 * @param parentLineModifierID
 * @returns
 */
const resolveLineModifier = (
    refSellState: ISaleDependencies,
    /** ID of menu that order line item is belong to. Sometimes the same product may have different prices in each menu. */
    lineMenuID: string,
    lineModifier: OrderLineModifierProcessContract,
    parentLineModifierID: string | null,
): OrderLineModifierSlimModel[] => {
    const modifierOption =
        refSellState.modifierOptionsMap[lineModifier.modifierOptionID!]
    const bindingModifier = refSellState.modifierList.find(
        (b) => b.entityModifierId === lineModifier.bindingModifierGroupID,
    )

    // For edge case
    if (!modifierOption || !bindingModifier) {
        return [
            {
                orderLineModifierID: v4(),
                type: ModifierOptionType.Add,
                quantity: lineModifier.quantity,
                priceAdd: 0,
                subtotal: 0,
                modifierOption: {
                    displayName: '#' + lineModifier.modifierOptionID,
                    type: ModifierOptionType.Add,
                    modifierGroupID: '',
                    sortNumber: 1,
                },
                modifierOptionID: lineModifier.modifierOptionID,
            },
        ]
    }

    let priceAdd = 0
    if (bindingModifier.hasAssociatedCost) {
        priceAdd = getModifierOptionPrice(
            modifierOption.modifierOptionID ?? '',
            lineMenuID,
            refSellState.priceListData,
        )
    }

    const currentLineModifierID = v4()

    const rootModifierLine: OrderLineModifierSlimModel = {
        orderLineModifierID: currentLineModifierID,
        parentOrderLineModifierID: parentLineModifierID,
        type: modifierOption.type,
        quantity: lineModifier.quantity,
        priceAdd: priceAdd,
        subtotal: priceAdd,
        bindingModifierGroupID: lineModifier.bindingModifierGroupID,
        modifierOptionID: lineModifier.modifierOptionID,
        modifierOption: {
            displayName: modifierOption.displayName ?? modifierOption.itemName,
            type: modifierOption.type,
            modifierGroupID: modifierOption.modifierGroupID,
            sortNumber: modifierOption.sortNumber,
            locationModifierOptions: [
                {
                    /** applied modifier option must be available */
                    isAvailableForSelection: true,
                },
            ],
        },
    }

    // resolve child modifier recursively
    const childModifierLines =
        lineModifier.subModifiers
            ?.map((childLine) =>
                resolveLineModifier(
                    refSellState,
                    lineMenuID,
                    childLine,
                    currentLineModifierID,
                ),
            )
            .flat() ?? []

    return [rootModifierLine, ...childModifierLines]
}

const toDiscountContract = (
    d: OrderLineDiscountSlimModel,
): OrderDiscountContract => {
    return {
        orderDiscountID: v4(),
        discountID: d.discountID,
        discountName: d.discountName,
        orderDiscountType: d.orderDiscountType,
        isPercentageDiscount: d.isPercentageDiscount ?? undefined,
        discountValue: d.discountValue!,
        discountAmount: d.discountAmount,
    }
}

const resolveDiscounts = (
    refSellState: ISaleDependencies,
    isLineDiscount: boolean,
    subtotal: number,
    quantity: number,
    discounts: OrderDiscountModel[],
): OrderLineDiscountSlimModel[] => {
    // remaining subtotal in this line
    let dSubtotal = new Decimal(subtotal)

    return discounts.map((d) => {
        const refDiscount = refSellState.discounts.find(
            (refDiscount) => refDiscount.discountID === d.discountID,
        )
        let name = refDiscount?.name
        if (!refDiscount) {
            if (d.isPercentage) name = `OFF ${d.discountValue}%`
            else name = `OFF ${d.discountValue}`
        }

        // Calculate discount amount from discount `d`
        let discountAmount: Decimal = new Decimal(0)
        if (dSubtotal.greaterThan(0)) {
            const dContract: DiscountContract = {
                type: d.isPercentage
                    ? DiscountType.Percentage
                    : DiscountType.FixedAmount,
                level: isLineDiscount
                    ? DiscountLevel.Item
                    : DiscountLevel.EntireOrder,
                value: d.discountValue,
            }
            discountAmount = calculateDiscountAmount(
                dContract,
                dSubtotal,
                quantity,
            )
            dSubtotal = dSubtotal.sub(discountAmount)
        }

        return {
            orderLineDiscountID: d.orderDiscountID!,
            discountID: d.discountID,
            discountName: name,
            orderDiscountType: d.discountType,
            isPercentageDiscount: d.isPercentage,
            discountValue: d.discountValue,
            discountAmount: discountAmount.toNumber(),
        }
    })
}

/**
 * build comp discount in `OrderLineDiscountSlimModel`
 * @param lineSubtotal Should be subTotal after discount
 * @param comp
 * @returns
 */
const resolveCompDiscount = (
    lineSubtotal: number,
    quantity: number,
    comp: VoidCompReasonModel,
): OrderLineDiscountSlimModel => {
    const discountOptions: DiscountContract = {
        type: comp.isPercentage
            ? DiscountType.Percentage
            : DiscountType.FixedAmount,
        level: DiscountLevel.Item,
        value: comp.value,
    }
    const discountAmount = calculateDiscountAmount(
        discountOptions,
        new Decimal(lineSubtotal),
        quantity,
    )
    return {
        orderLineDiscountID: v4(),
        orderDiscountType: OrderDiscountType.Comp,
        isPercentageDiscount: comp.isPercentage,
        discountValue: comp.value,
        discountAmount: discountAmount.toNumber(),
    }
}

const convertOfflineOrderLineToSlimModel = (
    dependencies: ISaleDependencies,
    line: OrderLineContract,
    /** Combo choice item which is being used by current line. Must not be null in case `line` is sub line (aka. combo sub-item line) */
    usingComboChoiceItem?: ComboItemViewmodel,
): OrderLineLookupContract => {
    const lineItem = resolveLineItem(dependencies, line.itemID!)
    const modifiers = line.orderLineModifiers
        ? line.orderLineModifiers
              .map((lm) =>
                  resolveLineModifier(
                      dependencies,
                      line.menuID ?? '',
                      lm,
                      null,
                  ),
              )
              .flat()
        : []

    // resolve combo VariantOptions
    let comboVariantOptions: OrderLineComboVariantOptionSlimModel[] = []
    if (usingComboChoiceItem)
        comboVariantOptions = resolveComboVariantOptions(
            line,
            usingComboChoiceItem,
        )

    let itemPrice = 0

    if (isOpenItemId(line.itemID)) {
        // use the price saved in cache-db
        itemPrice = line.price || 0
    } else {
        if (usingComboChoiceItem) {
            // if this line is line of combo sub item
            itemPrice =
                getItemPrice(
                    usingComboChoiceItem.comboChoiceItemId,
                    line.menuID ?? undefined,
                    dependencies.priceListData,
                ) +
                comboVariantOptions.reduce((acc, cvo) => acc + cvo.priceAdd!, 0)
        } else {
            itemPrice = getItemPrice(
                line.itemID!,
                line.menuID ?? undefined,
                dependencies.priceListData,
            )
        }
    }

    const itemSlimModel: OrderLineLookupContract = {
        // generate random ID as if it's already been saved
        orderLineID: v4(),
        price:
            usingComboChoiceItem || !Boolean(lineItem.baseItem?.itemID)
                ? itemPrice
                : 0,
        orderQuantity: line.orderQuantity,

        // will resolve this later step
        subtotal: 0,
        orderLineDiscountAmount: 0,
        isComp: false,
        isTaxInclusive: line.isTaxInclusive,
        displayName: line.displayName,

        sharedOrderLineID: line.sharedOrderLineID,
        item: lineItem,
        itemID: lineItem.baseItem?.itemID ?? line.itemID,
        orderLineModifiers: modifiers,
        note: line.note,
        subOrderLines:
            line.comboLineItems?.map((subLine, idx) => {
                /** find matched comboChoiceItem that is being used by the current sub line item */
                const listOfComboChoicesHavingSubLineItem =
                    dependencies.comboChoicesMap[line.itemID!]
                const comboChoiceUsingBySubLine = listOfComboChoicesHavingSubLineItem?.find(
                    (c) => c.comboChoiceID === subLine.comboChoiceID,
                )

                /** item's id which are ref in combo choice item are baseItemID (of variant product), or itemID (of normal product) */
                const result = findItemViewModel(
                    dependencies.itemsMap,
                    subLine.itemID!,
                )
                const itemIdrefInComboChoice =
                    result?.parentItem?.itemId ?? subLine.itemID!
                const comboChoiceItemUsingBySubLine = comboChoiceUsingBySubLine?.comboItems.find(
                    (ci) => ci.itemID === itemIdrefInComboChoice,
                )

                if (!comboChoiceItemUsingBySubLine) {
                    console.log([
                        listOfComboChoicesHavingSubLineItem,
                        comboChoiceUsingBySubLine,
                        comboChoiceItemUsingBySubLine,
                    ])
                    throw new Error(
                        `subLine[${idx}] contain item #${subLine.itemID} which does not longer exist`,
                    )
                }

                return convertOfflineOrderLineToSlimModel(
                    dependencies,
                    subLine,
                    comboChoiceItemUsingBySubLine,
                )
            }) ?? [],
        comboChoiceID: line.comboChoiceID,
        orderLineComboVariantOptions: comboVariantOptions,
    }

    const sumOfSubLineSubtotal =
        itemSlimModel.subOrderLines?.reduce(
            (acc, subLine) => acc + subLine.subtotal!,
            0,
        ) ?? 0

    itemSlimModel.subtotal =
        itemSlimModel.orderQuantity! *
        // line base Price (For combo main line this is combo's base price)
        (itemPrice +
            // Applied modifier
            modifiers.reduce((acc, m) => acc + m.subtotal!, 0) +
            // sum of subline price
            sumOfSubLineSubtotal)

    // resolve line discounts after resolved subtotal
    itemSlimModel.orderLineDiscounts = resolveDiscounts(
        dependencies,
        true,
        itemSlimModel.subtotal,
        itemSlimModel.orderQuantity!,
        line.discounts ?? [],
    )

    // resolve `itemDiscountAmount` field
    itemSlimModel.orderLineDiscountAmount = itemSlimModel.orderLineDiscounts.reduce(
        (acc, d) => acc + d.discountAmount!,
        0,
    )

    // resolve void/comp
    if (line.voidComp) {
        if (isComp(line.voidComp)) {
            itemSlimModel.isComp = true
            const lineSubTotalAfterDiscount =
                itemSlimModel.subtotal - itemSlimModel.orderLineDiscountAmount

            // Comp discount is part of `orderLineDiscounts`
            const compDiscount = resolveCompDiscount(
                lineSubTotalAfterDiscount,
                1,
                line.voidComp,
            )
            itemSlimModel.orderLineDiscounts.push(compDiscount)
            itemSlimModel.orderLineDiscountAmount += compDiscount.discountAmount!
        } else {
            itemSlimModel.status = OrderLineStatus.Voided
        }

        itemSlimModel.voidCompNotes = line.voidComp.note
        itemSlimModel.voidCompReason = line.voidComp.name
        itemSlimModel.voidCompReasonID = line.voidComp.voidCompReasonID
    }

    return itemSlimModel
}
// #endregion
